import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '@flowcore-water/ui';
import { useAuth } from '../../contexts/AuthContext';
import { LibraryList } from './LibraryList';
import { Flashcards } from './Flashcards';
import { TestCreator } from './TestCreator';
import { QuestionEditor } from './QuestionEditor';

type LibraryTab = 'documents' | 'flashcards' | 'tests' | 'questions';

const TABS: { id: LibraryTab; label: string; staffOnly?: boolean }[] = [
  { id: 'documents', label: 'Documents' },
  { id: 'flashcards', label: 'Flashcards' },
  { id: 'tests', label: 'Tests' },
  { id: 'questions', label: 'Question Bank', staffOnly: true },
];

export const LibraryPage: React.FC = () => {
  const { t } = useTheme();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as LibraryTab | null;
  const [activeTab, setActiveTab] = useState<LibraryTab>(
    tabParam && TABS.some((tab) => tab.id === tabParam) ? tabParam : 'documents'
  );

  const handleTabChange = (tab: LibraryTab) => {
    setActiveTab(tab);
    setSearchParams(tab === 'documents' ? {} : { tab });
  };

  return (
    <div className="space-y-6">
      {/* Tab Bar */}
      <div
        className="flex gap-1 rounded-lg p-1"
        style={{ background: 'rgba(0,0,0,0.25)' }}
      >
        {TABS.map((tab) => {
          if (tab.staffOnly && user?.role === 'individual') return null;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className="px-5 py-2 rounded-md text-sm font-medium transition-all"
              style={{
                background: isActive ? `${t.accent}18` : 'transparent',
                color: isActive ? t.accent : t.textSecondary,
                border: isActive ? `1px solid ${t.accent}40` : '1px solid transparent',
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'documents' && <LibraryList />}
      {activeTab === 'flashcards' && <Flashcards />}
      {activeTab === 'tests' && <TestCreator />}
      {activeTab === 'questions' && <QuestionEditor />}
    </div>
  );
};
